/**
 * EmptyStatePresets - Predefined Empty State Configurations
 * 
 * Predefined empty state components for common scenarios matching Figma designs.
 * Each preset includes appropriate icon, title, message, and action configuration.
 * 
 * Based on: EmptyStatesPage.tsx from Figma - ORO Nightfall Kit
 */

import EmptyState from './EmptyState';
import { MessageSquare, Users, Calendar, UserPlus, MapPin, CheckCircle } from 'lucide-react';

// Common preset props interface
interface PresetProps {
  onAction?: () => void;
  className?: string;
  variant?: 'default' | 'compact' | 'inline';
}

/**
 * No Messages Empty State
 * Used in: ChatArea, messaging contexts
 */
export function NoMessagesEmptyState({ onAction, className, variant = 'default' }: PresetProps) {
  return (
    <EmptyState
      variant={variant}
      icon={<MessageSquare />}
      title="No Messages Yet"
      message="Start a conversation with the club. Share your thoughts, ask questions, or plan your next move together."
      actionLabel="Send First Message"
      onAction={onAction}
      className={className}
    />
  );
}

/**
 * No Clubs Empty State  
 * Used in: Club discovery, user club lists
 */
export function NoClubsEmptyState({ onAction, className, variant = 'default' }: PresetProps) {
  return (
    <EmptyState
      variant={variant}
      icon={<Users />}
      title="No Clubs Yet"
      message="You haven't joined any clubs yet. Discover local communities and connect with people who share your passion for movement."
      actionLabel="Browse Clubs"
      onAction={onAction}
      className={className}
    />
  );
}

/**
 * No Events Empty State
 * Used in: Event listings, calendars, event feeds
 */
export function NoEventsEmptyState({ onAction, className, variant = 'default' }: PresetProps) {
  return (
    <EmptyState
      variant={variant}
      icon={<Calendar />}
      title="No Events Scheduled"
      message="There are no upcoming events right now. Check back soon or create your own event to get the community moving!"
      actionLabel="Create Event"
      onAction={onAction}
      className={className}
    />
  );
}

/**
 * No Members Empty State
 * Used in: Club member lists, member directories
 */
export function NoMembersEmptyState({ onAction, className, variant = 'default' }: PresetProps) {
  return (
    <EmptyState
      variant={variant}
      icon={<UserPlus />}
      title="No Members Yet"
      message="Be the first to join this club! Invite your friends to build an active community together."
      actionLabel="Invite Friends"
      onAction={onAction}
      className={className}
    />
  );
}

/**
 * No Upcoming Moves Empty State
 * Used in: Personal dashboard, activity feed, home screen
 */
export function NoUpcomingMovesEmptyState({ onAction, className, variant = 'default' }: PresetProps) {
  return (
    <EmptyState
      variant={variant}
      icon={<MapPin />}
      title="No Upcoming Moves"
      message="You don't have any upcoming events. Browse local events or create one to get moving!"
      actionLabel="Find Events"
      onAction={onAction}
      className={className}
    />
  );
}

/**
 * No Check-Ins Empty State
 * Used in: Event participation tracking, attendance lists
 */
export function NoCheckInsEmptyState({ onAction, className, variant = 'default' }: PresetProps) {
  return (
    <EmptyState
      variant={variant}
      icon={<CheckCircle />}
      title="No Check-Ins Yet"
      message="Check in to events to track your participation and connect with other attendees."
      actionLabel="View Events"
      onAction={onAction}
      className={className}
    />
  );
}

/**
 * Generic empty state for when presets don't fit
 * Maintains backward compatibility with existing EmptyState usage
 */
export function CustomEmptyState({ 
  icon, 
  title, 
  message, 
  actionLabel, 
  onAction, 
  className, 
  variant = 'default' 
}: {
  icon: React.ReactNode;
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
  variant?: 'default' | 'compact' | 'inline';
}) {
  return (
    <EmptyState
      variant={variant}
      icon={icon}
      title={title}
      message={message}
      actionLabel={actionLabel}
      onAction={onAction}
      className={className}
    />
  );
}

/**
 * Preset selector for easy switching between common empty states
 */
export type EmptyStatePreset = 
  | 'no-messages'
  | 'no-clubs' 
  | 'no-events'
  | 'no-members'
  | 'no-upcoming-moves'
  | 'no-check-ins';

export function EmptyStateFromPreset({ 
  preset, 
  onAction, 
  className, 
  variant = 'default' 
}: {
  preset: EmptyStatePreset;
  onAction?: () => void;
  className?: string;
  variant?: 'default' | 'compact' | 'inline';
}) {
  switch (preset) {
    case 'no-messages':
      return <NoMessagesEmptyState onAction={onAction} className={className} variant={variant} />;
    case 'no-clubs':
      return <NoClubsEmptyState onAction={onAction} className={className} variant={variant} />;
    case 'no-events':
      return <NoEventsEmptyState onAction={onAction} className={className} variant={variant} />;
    case 'no-members':
      return <NoMembersEmptyState onAction={onAction} className={className} variant={variant} />;
    case 'no-upcoming-moves':
      return <NoUpcomingMovesEmptyState onAction={onAction} className={className} variant={variant} />;
    case 'no-check-ins':
      return <NoCheckInsEmptyState onAction={onAction} className={className} variant={variant} />;
    default:
      return null;
  }
}